/* ============================================================
   SCNT — Search + Filter Overlay
   Vanilla JS, no libraries.

   - Opens when the header search trigger is clicked.
   - Closes on Esc / backdrop click / close button.
   - Locks body scroll while open, focuses the search input, basic focus trap.
   - Multi-select chips build a pre-filtered all-products URL using the
     Shopify list-filter syntax (filter.p.m.<key>=<value>, repeated per value).
   - Re-inits safely on DOMContentLoaded and on 'shopify:section:load'.
   ============================================================ */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function ScntSearchOverlay(root) {
    this.root = root;
    this.backdrop = root.querySelector('[data-scnt-search-backdrop]');
    this.panel = root.querySelector('.scnt-search-overlay__panel');
    this.input = root.querySelector('[data-scnt-search-input]');
    this.closeBtn = root.querySelector('[data-scnt-search-close]');
    this.clearBtn = root.querySelector('[data-scnt-search-clear]');
    this.resultsLink = root.querySelector('[data-scnt-search-results]');
    this.chips = Array.prototype.slice.call(root.querySelectorAll('[data-scnt-chip]'));
    this.allProductsUrl = root.getAttribute('data-all-products-url') || '/collections/all';

    /* Map<key, Set<value>> */
    this.selected = new Map();
    this.lastFocused = null;

    this.bind();
  }

  ScntSearchOverlay.prototype.bind = function () {
    var self = this;

    this.onKeydown = function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        self.close();
      } else if (e.key === 'Tab') {
        self.trapFocus(e);
      }
    };

    if (this.backdrop) {
      this.backdrop.addEventListener('click', function () {
        self.close();
      });
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function () {
        self.close();
      });
    }
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', function () {
        self.clear();
      });
    }

    this.chips.forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        self.toggleChip(chip);
      });
    });
  };

  ScntSearchOverlay.prototype.open = function () {
    if (this.root.hidden === false) return;
    this.lastFocused = document.activeElement;
    this.root.hidden = false;
    document.body.classList.add('scnt-search-locked');
    document.addEventListener('keydown', this.onKeydown);

    var self = this;
    // Focus the search input after the overlay paints.
    window.requestAnimationFrame(function () {
      if (self.input) {
        try {
          self.input.focus();
        } catch (err) {
          /* no-op */
        }
      }
    });
  };

  ScntSearchOverlay.prototype.close = function () {
    if (this.root.hidden === true) return;
    this.root.hidden = true;
    document.body.classList.remove('scnt-search-locked');
    document.removeEventListener('keydown', this.onKeydown);
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      try {
        this.lastFocused.focus();
      } catch (err) {
        /* no-op */
      }
    }
  };

  ScntSearchOverlay.prototype.toggle = function () {
    if (this.root.hidden) {
      this.open();
    } else {
      this.close();
    }
  };

  ScntSearchOverlay.prototype.toggleChip = function (chip) {
    var key = chip.getAttribute('data-key');
    var value = chip.getAttribute('data-value');
    if (!key || !value) return;

    var set = this.selected.get(key);
    if (!set) {
      set = new Set();
      this.selected.set(key, set);
    }

    if (set.has(value)) {
      set.delete(value);
      chip.classList.remove('is-selected');
      chip.setAttribute('aria-pressed', 'false');
      if (set.size === 0) this.selected.delete(key);
    } else {
      set.add(value);
      chip.classList.add('is-selected');
      chip.setAttribute('aria-pressed', 'true');
    }

    this.updateResultsHref();
  };

  ScntSearchOverlay.prototype.buildHref = function () {
    var params = [];
    this.selected.forEach(function (set, key) {
      set.forEach(function (value) {
        params.push(
          'filter.p.m.' + key + '=' + encodeURIComponent(value)
        );
      });
    });
    if (!params.length) return this.allProductsUrl;
    return this.allProductsUrl + '?' + params.join('&');
  };

  ScntSearchOverlay.prototype.updateResultsHref = function () {
    if (this.resultsLink) {
      this.resultsLink.setAttribute('href', this.buildHref());
    }
  };

  ScntSearchOverlay.prototype.clear = function () {
    this.selected.clear();
    this.chips.forEach(function (chip) {
      chip.classList.remove('is-selected');
      chip.setAttribute('aria-pressed', 'false');
    });
    this.updateResultsHref();
  };

  ScntSearchOverlay.prototype.trapFocus = function (e) {
    var focusable = Array.prototype.slice
      .call(this.root.querySelectorAll(FOCUSABLE))
      .filter(function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first || !this.root.contains(document.activeElement)) {
        e.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /* ---- Wiring ---- */

  /*
   * The real header search trigger lives in snippets/header-search.liquid
   * (a <summary> inside <details-modal>), which is not editable here.
   * header.liquid tags its wrapping render points with
   * [data-scnt-search-trigger]; we also delegate on the summary as a fallback.
   */
  function findInstance() {
    var el = document.querySelector('[data-scnt-search-overlay]');
    if (!el) return null;
    /* Portal to <body> so the fixed overlay always covers the full viewport,
       regardless of header stacking/transform contexts. */
    if (el.parentNode !== document.body) {
      document.body.appendChild(el);
    }
    if (!el.scntSearchOverlay) {
      el.scntSearchOverlay = new ScntSearchOverlay(el);
    }
    return el.scntSearchOverlay;
  }

  function attachTriggers() {
    if (window.__scntSearchOverlayDelegated) return;
    window.__scntSearchOverlayDelegated = true;

    document.addEventListener(
      'click',
      function (e) {
        // Only intercept if the overlay is enabled/present on the page.
        var instance = findInstance();
        if (!instance) return;

        var trigger = e.target.closest(
          '[data-scnt-search-trigger], [data-scnt-search-trigger] summary.header__icon--search'
        );

        if (trigger) {
          e.preventDefault();
          e.stopPropagation();
          instance.open();
        }
      },
      true /* capture: run before <details> toggles */
    );
  }

  function init() {
    findInstance();
    attachTriggers();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  /* Theme editor: re-init when the header section is reloaded. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector('[data-scnt-search-overlay]')) {
      var el = e.target.querySelector('[data-scnt-search-overlay]');
      el.scntSearchOverlay = null;
    }
    init();
  });
})();
